// src/comps/ChatWidget.js
import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import AppConst from "../apis/appconst";

export default function ChatWidget({ open, onClose }) {
  const [messages, setMessages] = useState([
    { role: "system", content: "You are a helpful assistant." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [lastError, setLastError] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open) {
      setLastError(null);
      // keep modal independent message history if you want; currently reuses messages
    }
  }, [open]);

  const scrollToBottom = () => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  const getApiUrl = () => {
    // You can set AppConst.aiChatUrl = '/ai/chat' (or '/api/ai/chat') in your AppConst file
    const suffix = (AppConst && (AppConst.aiChatUrl || AppConst.AI_CHAT_URL)) || "/ai/chat";
    // If BASE_URL is empty string or null, fallback to relative path for proxy
    const base = (AppConst && AppConst.BASE_URL) ? AppConst.BASE_URL.replace(/\/+$/, "") : "";
    return base + suffix;
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text) return;
    setLastError(null);

    const userMsg = { role: "user", content: text };
    const nextMessages = [...messages, userMsg];

    setMessages(nextMessages);
    setInput("");
    setLoading(true);

    try {
      const url = getApiUrl();

      // optional auth from session (pass user id or token to CI if you require)
      const stored = sessionStorage.getItem("ldata");
      let headers = { "Content-Type": "application/json" };
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (parsed?.token) headers["Authorization"] = `Bearer ${parsed.token}`;
          // optionally pass user id header:
          if (parsed?.user_id || parsed?.id) headers["X-User-Id"] = String(parsed.user_id ?? parsed.id);
        } catch (e) {
          // ignore parse errors
        }
      }

      const axiosResp = await axios.post(
        url,
        { messages: nextMessages },
        { headers, timeout: 45000 } // 45s
      );

      const data = axiosResp?.data;

      // Your CI returns { ok: true, message: { role, content }, raw: ... } ideally.
      if (data && data.ok && data.message) {
        setMessages(prev => [...prev, data.message]);
      } else if (data && data.choices && data.choices[0] && data.choices[0].message) {
        // raw OpenAI shape
        setMessages(prev => [...prev, data.choices[0].message]);
      } else if (data && typeof data === "string") {
        setMessages(prev => [...prev, { role: "assistant", content: data }]);
      } else {
        // fall back to showing the entire response object
        setMessages(prev => [...prev, { role: "assistant", content: JSON.stringify(data) }]);
      }

      scrollToBottom();
    } catch (err) {
      console.error("ChatWidget network/error:", err);

      // Extract useful error info
      let detail = "Unknown error";
      let status = null;
      if (axios.isAxiosError(err)) {
        status = err.response?.status ?? null;
        // try structured message from backend
        detail = err.response?.data?.error || err.response?.data?.message || err.message || String(err);
      } else {
        detail = err.message || String(err);
      }

      setLastError({ httpStatus: status, detail });
      setMessages(prev => [...prev, { role: "assistant", content: `Sorry — I couldn't reach the AI. (${detail})` }]);
      scrollToBottom();
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!open) return null;

  return (
    <div className="chat-modal" style={{
      position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 20000, background: "rgba(0,0,0,0.35)"
    }}>
      <div className="card" style={{ width: "520px", maxWidth: "95%", maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>AI Assistant</strong>
          <div className="d-flex align-items-center">
            {lastError && (
              <small style={{ color: "crimson", marginRight: 8 }}>
                Error: {lastError.httpStatus ? `HTTP ${lastError.httpStatus}` : ""} {lastError.detail}
              </small>
            )}
            <button className="btn btn-sm btn-light" onClick={onClose}>Close</button>
          </div>
        </div>

        <div className="card-body" style={{ overflowY: "auto", padding: "12px", flex: 1 }}>
          {messages.filter(m => m.role !== "system").map((m, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: "#777" }}>{m.role === "user" ? "You" : "AI"}</div>
              <div style={{
                background: m.role === "user" ? "#e7f1ff" : "#f3f4f6",
                padding: "8px 10px",
                borderRadius: 8,
                marginTop: 4,
                whiteSpace: "pre-wrap"
              }}>
                {m.content}
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        <div className="card-footer d-flex align-items-center">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask anything..."
            style={{ flex: 1, resize: "none", minHeight: 40, maxHeight: 120 }}
            className="form-control me-2"
            disabled={loading}
          />
          <button className="btn btn-primary" onClick={sendMessage} disabled={loading}>
            {loading ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
